import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { SignedNumber } from '@dndbeyond/character-common-components/es';
import { Collapsible, ItemName, DataOriginName, HtmlContent } from '@dndbeyond/character-components/es';
import {
    ArmorClassAdjustments,
    ArmorClassSupplier,
    characterActions,
    CharacterTheme,
    Constants,
    DataOrigin,
    FormatUtils,
    Item,
    ItemUtils,
    Modifier,
    ModifierUtils,
    RuleData,
    RuleDataUtils,
    rulesEngineSelectors,
    ValueLookup,
    ValueUtils,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions/sidebar';
import EditorBox from '../../../components/EditorBox';
import { SidebarHeader } from '../../../components/Sidebar';
import ValueEditor from '../../../components/ValueEditor';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils, PaneUtils } from '../../../utils';

interface Props extends DispatchProp {
    armorClassAdjustments: ArmorClassAdjustments;
    armorClassTotal: number;
    armorClassSuppliers: Array<ArmorClassSupplier>;
    valueLookup: ValueLookup;
    ruleData: RuleData;
    isReadonly: boolean;
    theme: CharacterTheme;
}
class ArmorManagePane extends React.PureComponent<Props, {}> {
    handleCustomDataUpdate = (key: number, value: string, source: string): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.valueSet(key, value, source));
    };

    handleItemDetailShow = (item: Item): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryPush(
                PaneComponentEnum.ITEM_DETAIL,
                PaneIdentifierUtils.generateItem(ItemUtils.getMappingId(item)),
            ),
        );
    };

    handleDataOriginShowDetail = (dataOrigin: DataOrigin): void => {
        const { dispatch } = this.props;

        let component = PaneUtils.getDataOriginComponentInfo(dataOrigin);
        if (component.type !== PaneComponentEnum.ERROR_404) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    renderCustom = (): React.ReactNode => {
        const { ruleData, valueLookup, isReadonly } = this.props;

        if (isReadonly) {
            return null;
        }

        let customizationValues: Array<Constants.AdjustmentTypeEnum> = [
            Constants.AdjustmentTypeEnum.OVERRIDE_AC,
            Constants.AdjustmentTypeEnum.OVERRIDE_BASE_ARMOR,
            Constants.AdjustmentTypeEnum.MAGIC_BONUS_AC,
            Constants.AdjustmentTypeEnum.MISC_BONUS_AC,
        ];

        return (
            <Collapsible layoutType={'minimal'} header="Customize" className="ct-armor-manage-pane__customize">
                <EditorBox>
                    <ValueEditor
                        dataLookup={ValueUtils.getDataLookup(valueLookup, customizationValues)}
                        onDataUpdate={this.handleCustomDataUpdate}
                        valueEditors={customizationValues}
                        ruleData={ruleData}
                    />
                </EditorBox>
            </Collapsible>
        );
    };

    renderInfo = (): React.ReactNode => {
        const { armorClassSuppliers, armorClassAdjustments, theme } = this.props;

        let hasOverrideAcAdjustment =
            armorClassAdjustments.overrideAc && armorClassAdjustments.overrideAc.value !== null;
        let itemsClasses: Array<string> = ['ct-armor-manage-pane__items'];
        if (hasOverrideAcAdjustment) {
            itemsClasses.push('ct-armor-manage-pane__items--overridden');
        }

        return (
            <div className={itemsClasses.join(' ')}>
                {armorClassSuppliers.map((armorSupplier, idx) => {
                    const { type, amount, extra, extraType } = armorSupplier;

                    if (!amount) {
                        return null;
                    }

                    let amountDisplay: React.ReactNode;
                    switch (type) {
                        case Constants.ArmorClassTypeEnum.ARMOR:
                        case Constants.ArmorClassTypeEnum.OVERRIDE_BASE_ARMOR:
                            amountDisplay = amount;
                            break;
                        default:
                            amountDisplay = <SignedNumber number={amount} />;
                    }

                    let extraDisplay: React.ReactNode;
                    switch (type) {
                        case Constants.ArmorClassTypeEnum.DEX_BONUS:
                            extraDisplay = extra === null ? '' : `Max ${extra}`;
                            break;

                        default:
                            switch (extraType) {
                                case Constants.ArmorClassExtraTypeEnum.MODIFIER:
                                    extraDisplay = (
                                        <DataOriginName
                                            dataOrigin={ModifierUtils.getDataOrigin(extra as Modifier)}
                                            onClick={this.handleDataOriginShowDetail}
                                            theme={theme}
                                        />
                                    );
                                    break;

                                case Constants.ArmorClassExtraTypeEnum.ITEM:
                                    if (extra) {
                                        extraDisplay = (
                                            <ItemName
                                                item={extra as Item}
                                                onClick={this.handleItemDetailShow.bind(this, extra)}
                                                theme={theme}
                                            />
                                        );
                                    } else {
                                        extraDisplay = 'None';
                                    }
                                    break;

                                case Constants.ArmorClassExtraTypeEnum.NUMBER:
                                    extraDisplay = extra ? FormatUtils.renderLocaleNumber(extra as number) : '';
                                    break;

                                case Constants.ArmorClassExtraTypeEnum.STRING:
                                default:
                                    extraDisplay = extra;
                            }
                            break;
                    }

                    return (
                        <div className="ct-armor-manage-pane__item" key={idx}>
                            <div className="ct-armor-manage-pane__item-value">{amountDisplay}</div>
                            <div className="ct-armor-manage-pane__item-label">
                                <span className="ct-armor-manage-pane__item-label-text">{type}</span>
                                {extraDisplay && (
                                    <span className="ct-armor-manage-pane__item-label-source">({extraDisplay})</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    renderDescription = (): React.ReactNode => {
        const { ruleData } = this.props;

        let rule = RuleDataUtils.getRule(Constants.RuleKeyEnum.ARMOR_CLASS, ruleData);

        if (!rule || !rule.description) {
            return null;
        }

        return <HtmlContent className="ct-armor-manage-pane__description" html={rule.description} />;
    };

    render() {
        const { armorClassTotal, armorClassAdjustments } = this.props;

        let hasOverrideAcAdjustment: boolean =
            armorClassAdjustments.overrideAc !== null && armorClassAdjustments.overrideAc.value !== null;
        let overrideAcAdjustmentSource: string | null =
            hasOverrideAcAdjustment && armorClassAdjustments.overrideAc ? armorClassAdjustments.overrideAc.notes : '';

        return (
            <div className="ct-armor-manage-pane">
                <SidebarHeader>
                    Armor Class: {armorClassTotal}
                    {hasOverrideAcAdjustment ? '*' : ''}
                    {hasOverrideAcAdjustment && (
                        <span className="ct-armor-manage-pane__heading-extra">
                            ({overrideAcAdjustmentSource ? overrideAcAdjustmentSource : 'Override'})
                        </span>
                    )}
                </SidebarHeader>
                {this.renderInfo()}
                {this.renderCustom()}
                {this.renderDescription()}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        armorClassAdjustments: rulesEngineSelectors.getAcAdjustments(state),
        armorClassTotal: rulesEngineSelectors.getAcTotal(state),
        armorClassSuppliers: rulesEngineSelectors.getArmorClassSuppliers(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        valueLookup: rulesEngineSelectors.getCharacterValueLookup(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
    };
}

export default connect(mapStateToProps)(ArmorManagePane);
